import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import FinancialProductRulesEditDetailsOutputDictionariesTable from './FinancialProductRulesEditDetailsOutputDictionariesTable';
import { useTranslation } from 'react-i18next';
import { useToast } from 'hooks/toasts/useToast';
import { getInt } from 'utils/functions';
import { useRouteMatch } from 'react-router-dom';
import { get, set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { FINANCIAL_PRODUCT_RULE_OUTPUT_DICTIONARY_PARAM_LIST_QUERY } from '../../../queries';
import { ADD_UPDATE_FINANCIAL_PRODUCT_RULE_OUTPUT_DICTIONARY, REMOVE_FINANCIAL_PRODUCT_RULE_OUTPUT_DICTIONARY } from '../../../mutations';
import { FINANCIAL_PRODUCT_RULE_OUTPUT_DICTIONARY_UPDATED, FINANCIAL_PRODUCT_RULE_OUTPUT_DICTIONARY_REMOVED } from '../../../subscriptions';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { financialProductRuleOutputDictionaryValidator } from '../../../validators';
import { emptyString } from 'utils/constants';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import { useGlobalIsDirty } from 'features/common/dirtyInfo/hooks';

const FinancialProductRulesEditDetailsOutputDictionariesContainer = ({ canWrite, outputDictionariesList, isSaved }) => {
  const addToast = useToast();
  const [t] = useTranslation();
  const match = useRouteMatch();
  const ruleId = match.params.ruleId && getInt(match.params.ruleId);

  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens([]);

  const [financialProductRuleOutputDictionaryValidation, validateFinancialProductRuleOutputDictionary] = useDirtyFieldValidation(
    financialProductRuleOutputDictionaryValidator
  );

  useEffect(() => {
    validateFinancialProductRuleOutputDictionary(lens |> get, dirtyInfo);
  }, [dirtyInfo, lens, validateFinancialProductRuleOutputDictionary]);

  const { data: financialProductRuleOutputDictionaryParamList } = useQuery(FINANCIAL_PRODUCT_RULE_OUTPUT_DICTIONARY_PARAM_LIST_QUERY);

  useEffect(() => {
    resetLens(
      outputDictionariesList?.map(element => {
        const elementLocal = { ...element };
        elementLocal.isInEditing = false;
        elementLocal.isSaving = false;
        elementLocal.modified = false;
        return elementLocal;
      }) || []
    );
  }, [resetLens, outputDictionariesList]);

  const handleAdd = useCallback(() => {
    set(lens)([
      ...(lens |> get),
      {
        FinancialProductRuleOutputDictionaryId: null,
        readOnlyYN: false,
        paramId: null,
        paramName: emptyString,
        filterValueId: null,
        filterValue: emptyString,
        modified: true,
        isInEditing: true
      }
    ]);
  }, [lens]);

  const handleCloseEdit = useCallback(
    orderNo => {
      const lensLocal = [...(lens |> get)];
      if (lensLocal[orderNo].FinancialProductRuleOutputDictionaryId === null) {
        lensLocal.splice(orderNo, 1);
      } else {
        const unmodifiedElement = outputDictionariesList.find(
          el => el.FinancialProductRuleOutputDictionaryId === lensLocal[orderNo].FinancialProductRuleOutputDictionaryId
        );
        lensLocal[orderNo] = { ...unmodifiedElement, modified: false, isInEditing: false };
      }
      set(lens)(lensLocal);
    },
    [lens, outputDictionariesList]
  );

  const [saveFinancialProductRuleOutputDictionary] = useMutation(ADD_UPDATE_FINANCIAL_PRODUCT_RULE_OUTPUT_DICTIONARY);

  const handleSave = useCallback(
    orderNo => {
      const lensLocal = [...(lens |> get)];
      lensLocal.forEach((element, index) => {
        if (index === orderNo) {
          element.isSaving = true;
        }
      });
      set(lens)(lensLocal);
      const elementTL = lensLocal[orderNo];
      saveFinancialProductRuleOutputDictionary({
        variables: {
          input: {
            financialProductRuleOutputDictionaryId: parseInt(elementTL.financialProductRuleOutputDictionaryId),
            financialProductRuleId: ruleId,
            readOnlyYN: elementTL.readOnlyYN,
            paramId: elementTL.paramId,
            filterValueId: elementTL.filterValueId,
            filterValue: elementTL.filterValue
          }
        }
      });
    },
    [lens, ruleId, saveFinancialProductRuleOutputDictionary]
  );

  useSubscription(FINANCIAL_PRODUCT_RULE_OUTPUT_DICTIONARY_UPDATED, {
    onData: ({ data: { data }, client }) => {
      const lensLocal = [...(lens |> get)];
      addToast(t('FinancialProduct.Details.Rules.Details.OutputDictionaries.FinancialProductRuleOutputDictionarySaved'), 'success');
      lensLocal.forEach((element, index) => {
        if (index === data.financialProductRuleOutputDictionaryUpdated.orderNo) {
          element.financialProductRuleOutputDictionaryId = data.financialProductRuleOutputDictionaryUpdated.outputDictionaryId;
          element.isInEditing = false;
          element.isSaving = false;
          element.modified = false;
        }
      });
      resetLens(lensLocal);
      client.cache.evict({
        id: `FinancialProductRuleEditItem:{"financialProductRuleId":${ruleId}}`
      });
      client.cache.gc();
    }
  });

  const [deleteFinancialProductRuleOutputDictionary] = useMutation(REMOVE_FINANCIAL_PRODUCT_RULE_OUTPUT_DICTIONARY);

  const handleDelete = useCallback(
    outputDictionaryId => {
      if (window.confirm(t('FinancialProduct.Details.Rules.Details.OutputDictionaries.DeleteFinancialProductRuleOutputDictionary'))) {
        if (outputDictionaryId) {
          deleteFinancialProductRuleOutputDictionary({
            variables: {
              input: { outputDictionaryId }
            }
          });
        } else {
          resetLens(outputDictionariesList);
        }
      }
    },
    [deleteFinancialProductRuleOutputDictionary, outputDictionariesList, resetLens, t]
  );

  useSubscription(FINANCIAL_PRODUCT_RULE_OUTPUT_DICTIONARY_REMOVED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('FinancialProduct.Details.Rules.Details.OutputDictionaries.FinancialProductRuleOutputDictionaryRemoved'), 'success');
      const lensLocal = [...(lens |> get)];
      const objTL = lensLocal.filter(
        el => el.financialProductRuleOutputDictionaryId !== data.financialProductRuleOutputDictionaryDeleted.outputDictionaryId
      );
      resetLens(objTL);
      client.cache.evict({
        id: `FinancialProductRuleEditItem:{"financialProductRuleId":${ruleId}}`
      });
    }
  });

  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  useGlobalIsDirty(isDirty);

  return financialProductRuleOutputDictionaryParamList?.financialProductRuleOutputDictionaryParamList ? (
    <FinancialProductRulesEditDetailsOutputDictionariesTable
      canWrite={canWrite}
      financialProductRuleOutputDictionaryParamList={
        financialProductRuleOutputDictionaryParamList?.financialProductRuleOutputDictionaryParamList
      }
      outputDictionariesList={lens}
      isSaved={isSaved}
      onAdd={handleAdd}
      onCloseEdit={handleCloseEdit}
      onDelete={handleDelete}
      onSave={handleSave}
      financialProductRuleOutputDictionaryValidation={financialProductRuleOutputDictionaryValidation}
    ></FinancialProductRulesEditDetailsOutputDictionariesTable>
  ) : (
    <></>
  );
};

FinancialProductRulesEditDetailsOutputDictionariesContainer.propTypes = {
  canWrite: PropTypes.bool.isRequired,
  outputDictionariesList: PropTypes.array.isRequired,
  isSaved: PropTypes.bool.isRequired
};

export default FinancialProductRulesEditDetailsOutputDictionariesContainer;
